import { FetchOptionsCallback } from 'global';
import { logError } from 'utils/logger';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import {
  CreateStageNoteResponse,
  CreateStageNoteVariables,
  UploadStageDocumentResponse,
  UploadStageDocumentVariables,
  RemoveStageDocumentResponse,
  RemoveStageDocumentVariables,
  bpfAPI,
} from '../index';

export function useBPFStages() {
  const { showNotification } = useNotifications();

  const [onCreateStageNote, { loading: isNoteCreating }] = useMutation<
    CreateStageNoteResponse,
    CreateStageNoteVariables
  >(bpfAPI.createStageNote);

  const [onUploadStageDocument, { loading: isDocumentUploading }] = useMutation<
    UploadStageDocumentResponse,
    UploadStageDocumentVariables
  >(bpfAPI.uploadStageDocument);

  const [onRemoveStageDocument, { loading: isDocumentRemoving }] = useMutation<
    RemoveStageDocumentResponse,
    RemoveStageDocumentVariables
  >(bpfAPI.removeStageDocument);

  const handleCreateStageNote = async (
    dto: CreateStageNoteVariables['input'],
    fetchOptions?: FetchOptionsCallback<typeof bpfAPI, CreateStageNoteResponse, CreateStageNoteVariables>
  ) => {
    try {
      const { data, errors } = await onCreateStageNote({
        variables: {
          input: dto,
        },
        ...(typeof fetchOptions === 'function' ? fetchOptions(bpfAPI) : {}),
      });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleCreateCloseReasons error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return null;
      }

      showNotification('Note created successfully.');

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  const handleUploadStageDocument = async (
    dto: UploadStageDocumentVariables['input'],
    fetchOptions?: FetchOptionsCallback<typeof bpfAPI, UploadStageDocumentResponse, UploadStageDocumentVariables>
  ) => {
    try {
      const { data, errors } = await onUploadStageDocument({
        variables: {
          input: dto,
        },
        ...(typeof fetchOptions === 'function' ? fetchOptions(bpfAPI) : {}),
      });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleUploadStageDocument error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return null;
      }

      showNotification('Document uploaded successfully.');

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  const handleRemoveStageDocument = async (
    id: string,
    fetchOptions?: FetchOptionsCallback<typeof bpfAPI, RemoveStageDocumentResponse, RemoveStageDocumentVariables>
  ) => {
    try {
      const { data, errors } = await onRemoveStageDocument({
        variables: {
          input: { id },
        },
        ...(typeof fetchOptions === 'function' ? fetchOptions(bpfAPI) : {}),
      });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleRemoveStageDocument error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return null;
      }

      showNotification('Document removed successfully.');

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  return {
    isNoteCreating,
    isDocumentUploading,
    isDocumentRemoving,
    handleCreateStageNote,
    handleUploadStageDocument,
    handleRemoveStageDocument,
  };
}
